import { cn } from '@heidi/utils';

import type {
  AvatarBadgeColor,
  AvatarBadgePosition,
  AvatarColor,
  AvatarSize,
} from './avatar.types';

export const avatarRootClasses = cn(
  // `size-10` is the `lg` (40px) default; `md`, `sm`, and `xs` override it.
  'group/avatar relative flex size-10 shrink-0 overflow-hidden rounded-full',
  'data-[size=md]:size-8 data-[size=sm]:size-6 data-[size=xs]:size-[18px]',
  'data-[shape=square]:rounded-[30%] data-[shape=square]:after:rounded-[30%]',
  // Empty variant: dashed outline placeholder, no ::after polish.
  'data-[color=empty]:border data-[color=empty]:border-dashed data-[color=empty]:border-border-strong',
  'data-[color=empty]:after:hidden',
  // Non-empty avatars get a subtle inset border ::after for edge definition.
  'select-none',
  'after:pointer-events-none after:absolute after:inset-0 after:rounded-full',
  'after:border after:border-black/[0.08] after:mix-blend-darken',
  'newBrandDark:after:border-white/[0.12] newBrandDark:after:mix-blend-lighten'
);

export const avatarImageClasses = cn(
  'aspect-square size-full object-cover',
  'group-data-[shape=square]/avatar:rounded-[30%]'
);

/** Fallback palette keyed by `AvatarColor` */
const fallbackColorStyles: Record<AvatarColor, string> = {
  neutral:
    'group-data-[color=neutral]/avatar:bg-entity-neutral group-data-[color=neutral]/avatar:text-entity-neutral-foreground',
  green:
    'group-data-[color=green]/avatar:bg-entity-green group-data-[color=green]/avatar:text-entity-green-foreground',
  blue: 'group-data-[color=blue]/avatar:bg-entity-blue group-data-[color=blue]/avatar:text-entity-blue-foreground',
  yellow:
    'group-data-[color=yellow]/avatar:bg-entity-yellow group-data-[color=yellow]/avatar:text-entity-yellow-foreground',
  empty:
    'group-data-[color=empty]/avatar:bg-surface group-data-[color=empty]/avatar:text-text-tertiary',
};

export const avatarFallbackClasses = cn(
  'flex size-full items-center justify-center rounded-full bg-background-secondary text-sm',
  'group-data-[shape=square]/avatar:rounded-[30%]',
  'group-data-[size=md]/avatar:text-xs',
  'group-data-[size=sm]/avatar:text-[10px]',
  'group-data-[size=xs]/avatar:text-[10px]',
  ...Object.values(fallbackColorStyles)
);

/**
 * Badge marker shared base. Sits on the avatar rim with a 1px ring in the
 * container surface so it reads as separated from the avatar (and from
 * neighbours in an `AvatarGroup`). The ring colour resolves from the
 * `--avatar-ring-bg` CSS var, defaulting to `surface`.
 */
export const avatarBadgeBaseClasses = cn(
  'pointer-events-none absolute z-10 inline-flex select-none items-center justify-center rounded-full',
  'ring-1 ring-[color:var(--avatar-ring-bg,hsl(var(--twc-surface)))]'
);

/** Corner placement — logical (`start`/`end`) so it mirrors in RTL. */
export const avatarBadgePositionClasses: Record<AvatarBadgePosition, string> = {
  'top-start': 'top-0 start-0',
  'top-end': 'top-0 end-0',
  'bottom-start': 'bottom-0 start-0',
  'bottom-end': 'bottom-0 end-0',
};

/** Entity-palette fills (theme-aware) for the badge. */
export const avatarBadgeColorClasses: Record<AvatarBadgeColor, string> = {
  neutral: 'bg-entity-neutral text-entity-neutral-foreground',
  green: 'bg-entity-green text-entity-green-foreground',
  blue: 'bg-entity-blue text-entity-blue-foreground',
  yellow: 'bg-entity-yellow text-entity-yellow-foreground',
  red: 'bg-entity-red text-entity-red-foreground',
  orange: 'bg-entity-orange text-entity-orange-foreground',
  purple: 'bg-entity-purple text-entity-purple-foreground',
  pink: 'bg-entity-pink text-entity-pink-foreground',
};

/**
 * Status-dot diameter per avatar `size`. The dot is the smallest marker and
 * scales down with the avatar; it carries no content.
 */
export const avatarBadgeDotSizeClasses: Record<AvatarSize, string> = {
  xs: 'size-2',
  sm: 'size-2.5',
  md: 'size-3',
  lg: 'size-3.5',
};

/**
 * Icon / image badge extras, applied on top of the dot diameter
 * (`avatarBadgeDotSizeClasses[size]`) so an icon badge is the same size as a
 * dot — it never grows. `overflow-hidden` clips an image to the circle and a
 * bare SVG auto-sizes down to fit the dot-sized badge.
 */
export const avatarBadgeIconSizeClasses: Record<AvatarSize, string> = {
  xs: cn('overflow-hidden', '[&>svg:not([class*=size-])]:size-1.5'),
  sm: cn('overflow-hidden', '[&>svg:not([class*=size-])]:size-1.5'),
  md: cn('overflow-hidden', '[&>svg:not([class*=size-])]:size-2'),
  lg: cn('overflow-hidden', '[&>svg:not([class*=size-])]:size-2.5'),
};
